/**
 * Journal — US 01.10/01.11/01.12, pure DOM. Opened by intent (J). Every
 * line it shows came from the server's `journal` op, generated from
 * authoritative state — this panel cannot reveal undiscovered content
 * because it never receives any.
 */
import { worldBus } from '@shared/bus.js';
import { callDialogue } from '@transport/api.js';

interface JournalData {
  entries: { id: string; npcId: string; text: string; status: 'active' | 'completed' }[];
  pendingPickups: { npcId: string; count: number; location: string }[];
  deathDrop: { location: string; count: number } | null;
  pictures: { found: number; available: number };
  secrets: { found: number; total: number };
  landmarks: { id: string; name: string }[];
  travel: { fare: number };
  hints: { available: boolean; costs: readonly number[] };
}

export function initJournalPanel(root: HTMLElement, onStateChanged: () => void): void {
  const panel = document.createElement('div');
  panel.className = 'wca-journal hidden';
  root.appendChild(panel);

  const npcNames = new Map<string, string>();
  void fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/npc_public?select=id,name`, {
    headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY ?? '' },
  })
    .then((r) => r.json())
    .then((rows: { id: string; name: string }[]) => {
      for (const row of rows) npcNames.set(row.id, row.name);
    })
    .catch(() => undefined);

  let open = false;
  let data: JournalData | null = null;
  let filter: 'active' | 'completed' = 'active';
  let hintText: string | null = null;

  function section(title: string): HTMLElement {
    const h = document.createElement('div');
    h.className = 'wca-journal-section';
    h.textContent = title;
    return h;
  }

  function render(): void {
    panel.innerHTML = '';
    const head = document.createElement('div');
    head.className = 'wca-inventory-head';
    head.textContent = 'Journal';
    panel.appendChild(head);
    if (!data) {
      panel.appendChild(document.createTextNode('Loading…'));
      return;
    }

    // Objectives, filterable active/completed (US 01.10).
    panel.appendChild(section('Objectives'));
    const toggle = document.createElement('button');
    toggle.className = 'wca-choice wca-journal-filter';
    toggle.textContent = filter === 'active' ? 'Showing: active' : 'Showing: completed';
    toggle.addEventListener('click', () => {
      filter = filter === 'active' ? 'completed' : 'active';
      render();
    });
    panel.appendChild(toggle);
    const list = data.entries.filter((e) => e.status === filter);
    if (list.length === 0) {
      const p = document.createElement('p');
      p.textContent = filter === 'active' ? 'Nothing on your plate.' : 'Nothing finished yet.';
      panel.appendChild(p);
    }
    for (const e of list) {
      const p = document.createElement('p');
      p.textContent = `${e.status === 'completed' ? '✔' : '•'} ${e.text} — ${npcNames.get(e.npcId) ?? e.npcId}`;
      panel.appendChild(p);
    }

    // Left-behind things with locations (US 01.06/01.07).
    if (data.pendingPickups.length > 0 || data.deathDrop) {
      panel.appendChild(section('Left behind'));
      for (const b of data.pendingPickups) {
        const p = document.createElement('p');
        p.textContent = `${b.count} item(s) waiting with ${npcNames.get(b.npcId) ?? b.npcId}, ${b.location}`;
        panel.appendChild(p);
      }
      if (data.deathDrop) {
        const p = document.createElement('p');
        p.textContent = `Your dropped bag (${data.deathDrop.count} item(s)), ${data.deathDrop.location}`;
        panel.appendChild(p);
      }
    }

    // Collection progress (US 01.10).
    panel.appendChild(section('Collection'));
    const prog = document.createElement('p');
    prog.textContent =
      `Pictures ${data.pictures.found}/${data.pictures.available}` +
      (data.secrets.total > 0 ? ` · Secrets ${data.secrets.found}/${data.secrets.total}` : '');
    panel.appendChild(prog);

    // Fast travel between discovered landmarks (US 01.11).
    panel.appendChild(section('Travel'));
    const home = document.createElement('button');
    home.className = 'wca-choice';
    home.textContent = 'Return home (free)';
    home.addEventListener('click', () => void travel('home'));
    panel.appendChild(home);
    for (const l of data.landmarks) {
      const b = document.createElement('button');
      b.className = 'wca-choice';
      b.textContent = `Taxi to ${l.name} (${data.travel.fare})`;
      b.addEventListener('click', () => void travel(l.id));
      panel.appendChild(b);
    }

    // Escalating hints (US 01.12) — player-triggered, never automatic.
    panel.appendChild(section('Stuck?'));
    if (!data.hints.available) {
      const p = document.createElement('p');
      p.textContent = 'Hints unlock when you have been stuck for a while.';
      panel.appendChild(p);
    } else {
      const labels = [
        'Who? (free)',
        `What? (${data.hints.costs[1]})`,
        `Where? (${data.hints.costs[2]})`,
      ];
      labels.forEach((label, i) => {
        const b = document.createElement('button');
        b.className = 'wca-choice';
        b.textContent = label;
        b.addEventListener('click', () => void hint((i + 1) as 1 | 2 | 3));
        panel.appendChild(b);
      });
    }
    if (hintText) {
      const p = document.createElement('p');
      p.className = 'wca-hint-text';
      p.textContent = hintText;
      panel.appendChild(p);
    }
  }

  async function travel(landmarkId: string): Promise<void> {
    const r = await callDialogue<{ x: number; y: number }>({ op: 'travel', landmarkId });
    if (r.ok) {
      worldBus.emit('travel-completed', { x: r.result.x, y: r.result.y });
      onStateChanged();
      toggle_close();
    }
  }

  async function hint(tier: 1 | 2 | 3): Promise<void> {
    const r = await callDialogue<{ text: string; cost: number }>({ op: 'hint', tier });
    if (r.ok) {
      hintText = r.result.text;
      onStateChanged();
      void refresh();
    }
  }

  function refresh(): Promise<void> {
    return callDialogue<JournalData>({ op: 'journal' }).then((res) => {
      if (res.ok) {
        data = res.result;
        if (open) render();
      }
    });
  }

  function toggle_close(): void {
    open = false;
    panel.classList.add('hidden');
  }

  worldBus.on('ui-intent', ({ intent }) => {
    if (intent === 'OPEN_JOURNAL') {
      open = !open;
      panel.classList.toggle('hidden', !open);
      if (open) {
        render();
        void refresh();
      }
    }
    if (intent === 'CANCEL' && open) toggle_close();
  });
}
